import { type z } from 'zod'

import { schemas } from '~interface/zod-schema'

import { type Identity0, type ValueType, ValueTypeEnum } from './cases'
import {
  type ExpressionId,
  type IdentityId,
  type InputStepId,
  type ReviewBlockId,
  type ValueId,
} from './idRegistry'
import { type InputSubstep2 } from './processes'
import { type Value0 } from './values'

// Values
export const OperatorType = schemas.OperatorTypeEnum
export const ExpressionType = schemas.ExpressionTypeEnum
export const CaseIdentifierType = schemas.CaseIdentifierTypeEnum

// Types
export type Expression = z.infer<typeof schemas.Expression>
export type ExpressionTypeEnum = z.infer<typeof schemas.ExpressionTypeEnum>
export type OperatorTypeEnum = z.infer<typeof schemas.OperatorTypeEnum>
export type CaseIdentifierTypeEnum = z.infer<typeof schemas.CaseIdentifierTypeEnum>

export enum ExpressionCreateTypeEnum {
  AnyEmpty = 'AnyEmpty',
  Empty = 'Empty',
  ExistingFormula = 'ExistingFormula',
  Addition = 'Addition',
  And = 'And',
  CaseIdentifier = 'CaseIdentifier',
  Conditional = 'Conditional',
  Equals = 'Equals',
  FileConstant = 'FileConstant',
  GreaterThan = 'GreaterThan',
  IdentityConstant = 'IdentityConstant',
  InputChoice = 'InputChoice',
  InputValue = 'InputValue',
  LessThan = 'LessThan',
  Maximum = 'Maximum',
  Minimum = 'Minimum',
  Multiplication = 'Multiplication',
  Or = 'Or',
  RawDatetimeValue = 'RawDatetimeValue',
  RawNumberValue = 'RawNumberValue',
  ReviewOutcome = 'ReviewOutcome',
  Subtraction = 'Subtraction',
  Today = 'Today',
}

export type ExpressionCreateType =
  | IAnyEmptyCreate
  | IEmptyCreate
  | IExistingFormulaCreate
  | IInputChoiceCreate
  | IInputValueCreate
  | IBinaryOperatorCreate
  | IConditionalOperatorCreate
  | IRawDatetimeValueCreate
  | IRawNumberValueCreate
  | ITodayCreate
  | IIdentityConstantCreate
  | IFileConstantCreate
  | ICaseIdentifierCreate
  | IReviewOutcomeCreate

export interface IAnyEmptyCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.AnyEmpty
  matching: number[]
}

export interface IEmptyCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.Empty
  type: ValueType
}

export interface IExistingFormulaCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.ExistingFormula
  type: ValueType | null
  matching: number[]
  selectedExpression: Expression | null
  custom?: boolean
}

export interface IInputChoiceCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.InputChoice
  choices: string[]
  selected: string | null
  reviewOutcome?: boolean
}

export interface IInputValueCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.InputValue
  type: ValueType | null
  matching: number[]
  selectedInput: InputSubstep2 | null
}

export interface IBinaryOperatorCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum
  type: OperatorTypeEnum
  left: number
  right: number
}

export interface IConditionalOperatorCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.Conditional
  if: number
  then: number
  else: number
  type: ValueType | null
}

export interface IRawNumberValueCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.RawNumberValue
  numberValue: number
}

export interface IRawDatetimeValueCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.RawDatetimeValue
  datetimeValue: string
}

export interface ITodayCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.Today
}

export interface ICaseIdentifierCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.CaseIdentifier
  caseIdentifierType: CaseIdentifierTypeEnum
}

export interface IReviewOutcomeCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.ReviewOutcome
  reviewBlockId: ReviewBlockId | null
}

export interface IIdentityConstantCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.IdentityConstant
  identity: Identity0 | null
}

export interface IFileConstantCreate {
  id: number
  expressionType: ExpressionCreateTypeEnum.FileConstant
  url: string | null
  previewUrl: string | null
}

export const expressionToExpressionCreate = (
  newId: number,
  expressionId: ExpressionId,
  expressionMap: Map<ExpressionId, Expression>,
  idMap: Map<ExpressionId, number>,
  inputStepMap: Map<InputStepId, InputSubstep2>,
  valueMap: Map<ValueId, Value0>,
  identityMap: Map<IdentityId, Identity0>,
): ExpressionCreateType | null => {
  const expression = expressionMap.get(expressionId)
  if (!expression || expression.case_creator) return null
  else if (expression.custom) {
    return {
      id: newId,
      expressionType: ExpressionCreateTypeEnum.ExistingFormula,
      type: expression.type ?? null,
      matching: [],
      selectedExpression: expression,
      custom: true,
    }
  } else if (expression.collapsed && newId !== 0) {
    // A collapsed expression is rendered by name as an existing formula rather than expanded,
    // except the root expression being viewed (newId === 0), which is always expanded.
    return {
      id: newId,
      expressionType: ExpressionCreateTypeEnum.ExistingFormula,
      type: expression.type ?? null,
      matching: [],
      selectedExpression: expression,
    }
  } else if (expression.constant) {
    if (expression.type === ValueTypeEnum.enum.number) {
      return {
        id: newId,
        expressionType: ExpressionCreateTypeEnum.RawNumberValue,
        numberValue:
          Number(valueMap.get(expression.constant as ValueId)?.number_value?.number) ?? 0,
      }
    } else if (expression.type === ValueTypeEnum.enum.text) {
      let choices: string[] = []
      let reviewOutcome = false
      for (const [, expr] of expressionMap) {
        const isParent =
          expr.operator == OperatorType.enum.equals &&
          (expr.parameter_one == expressionId || expr.parameter_two == expressionId)

        if (isParent) {
          let inputExpressionId: ExpressionId | null = null

          if (expr.parameter_one === expressionId) {
            inputExpressionId = expr.parameter_two as ExpressionId
          } else {
            inputExpressionId = expr.parameter_one as ExpressionId
          }

          const inputExpression = expressionMap.get(inputExpressionId as ExpressionId)
          if (inputExpression?.review_outcome) {
            reviewOutcome = true
          } else if (inputExpression?.input_value) {
            const inputSubstep = inputStepMap.get(inputExpression.input_value as InputStepId)

            if (inputSubstep) {
              choices = inputSubstep.choices
                .map((choice) => choice.value.text_value?.text)
                .filter((text): text is string => text !== undefined)
            }
          }
          break
        }
      }
      return {
        id: newId,
        expressionType: ExpressionCreateTypeEnum.InputChoice,
        choices: choices,
        selected: valueMap.get(expression.constant as ValueId)?.text_value?.text ?? null,
        reviewOutcome,
      }
    } else if (expression.type === ValueTypeEnum.enum.datetime) {
      return {
        id: newId,
        expressionType: ExpressionCreateTypeEnum.RawDatetimeValue,
        datetimeValue: valueMap.get(expression.constant as ValueId)?.datetime_value?.datetime ?? '',
      }
    } else if (expression.type === ValueTypeEnum.enum.identity) {
      const identityId = valueMap.get(expression.constant as ValueId)?.identity_value
        ?.identity as IdentityId
      return {
        id: newId,
        expressionType: ExpressionCreateTypeEnum.IdentityConstant,
        identity: identityMap.get(identityId) as Identity0 | null,
      }
    } else if (expression.type === ValueTypeEnum.enum.file) {
      const fileUrl = valueMap.get(expression.constant as ValueId)?.file_value?.files[0] ?? null
      return {
        id: newId,
        expressionType: ExpressionCreateTypeEnum.FileConstant,
        url: fileUrl?.split('?')[0] ?? null,
        previewUrl: fileUrl,
      }
    }
  } else if (expression.input_value) {
    const inputSubstep = inputStepMap.get(expression.input_value as InputStepId)
    if (!inputSubstep) return null
    return {
      id: newId,
      expressionType: ExpressionCreateTypeEnum.InputValue,
      type: inputSubstep.type,
      matching: [],
      selectedInput: inputSubstep,
    }
  } else if (expression.operator) {
    const parameterOne = idMap.get(expression.parameter_one as ExpressionId)
    const parameterTwo = idMap.get(expression.parameter_two as ExpressionId)
    const parameterThree = expression.parameter_three
      ? idMap.get(expression.parameter_three as ExpressionId)
      : null
    if (!parameterOne || !parameterTwo) return null

    switch (expression.operator) {
      case OperatorType.enum.addition:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.Addition,
          type: OperatorType.enum.addition,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.and:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.And,
          type: OperatorType.enum.and,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.conditional:
        const conditionalType = expressionMap.get(expression.parameter_two as ExpressionId)?.type
        if (!conditionalType || !parameterThree) return null
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.Conditional,
          if: parameterOne,
          then: parameterTwo,
          else: parameterThree,
          type: conditionalType,
        }
      case OperatorType.enum.equals:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.Equals,
          type: OperatorType.enum.equals,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.greater_than:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.GreaterThan,
          type: OperatorType.enum.greater_than,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.less_than:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.LessThan,
          type: OperatorType.enum.less_than,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.maximum:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.Maximum,
          type: OperatorType.enum.maximum,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.minimum:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.Minimum,
          type: OperatorType.enum.minimum,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.multiplication:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.Multiplication,
          type: OperatorType.enum.multiplication,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.or:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.Or,
          type: OperatorType.enum.or,
          left: parameterOne,
          right: parameterTwo,
        }
      case OperatorType.enum.subtraction:
        return {
          id: newId,
          expressionType: ExpressionCreateTypeEnum.Subtraction,
          type: OperatorType.enum.subtraction,
          left: parameterOne,
          right: parameterTwo,
        }
      default:
        return null
    }
  } else if (expression.today) {
    if (expression.type === ValueTypeEnum.enum.datetime) {
      return {
        id: newId,
        expressionType: ExpressionCreateTypeEnum.Today,
      }
    }
  } else if (expression.case_identifier) {
    if (expression.type === ValueTypeEnum.enum.text) {
      return {
        id: newId,
        expressionType: ExpressionCreateTypeEnum.CaseIdentifier,
        caseIdentifierType: expression.case_identifier,
      }
    }
  } else if (expression.review_outcome) {
    return {
      id: newId,
      expressionType: ExpressionCreateTypeEnum.ReviewOutcome,
      reviewBlockId: expression.review_outcome as ReviewBlockId,
    }
  }
  return null
}

export function getDirectChildren(
  expressionMap: Record<number, ExpressionCreateType>,
  id: number,
): number[] {
  switch (expressionMap[id].expressionType) {
    case ExpressionCreateTypeEnum.Addition:
    case ExpressionCreateTypeEnum.And:
    case ExpressionCreateTypeEnum.Equals:
    case ExpressionCreateTypeEnum.GreaterThan:
    case ExpressionCreateTypeEnum.LessThan:
    case ExpressionCreateTypeEnum.Maximum:
    case ExpressionCreateTypeEnum.Minimum:
    case ExpressionCreateTypeEnum.Multiplication:
    case ExpressionCreateTypeEnum.Or:
    case ExpressionCreateTypeEnum.Subtraction:
      const binaryExpressionCreate = expressionMap[id] as IBinaryOperatorCreate
      return [binaryExpressionCreate.left, binaryExpressionCreate.right]
    case ExpressionCreateTypeEnum.Conditional:
      const conditionalExpressionCreate = expressionMap[id] as IConditionalOperatorCreate
      return [
        conditionalExpressionCreate.if,
        conditionalExpressionCreate.then,
        conditionalExpressionCreate.else,
      ]
    default:
      return []
  }
}

export function getAllChildren(
  expressionMap: Record<number, ExpressionCreateType>,
  id: number,
): number[] {
  const children = getDirectChildren(expressionMap, id)
  let allChildren = children
  for (const child of children) {
    allChildren = allChildren.concat(getAllChildren(expressionMap, child))
  }
  return allChildren
}

export function getType(
  expressionMap: Record<number, ExpressionCreateType>,
  id: number,
): ValueType | null {
  const expression = expressionMap[id]
  if (!expression) return null
  switch (expression.expressionType) {
    case ExpressionCreateTypeEnum.Empty:
      return (expression as IEmptyCreate).type
    case ExpressionCreateTypeEnum.Addition:
    case ExpressionCreateTypeEnum.Maximum:
    case ExpressionCreateTypeEnum.Minimum:
    case ExpressionCreateTypeEnum.Multiplication:
    case ExpressionCreateTypeEnum.RawNumberValue:
    case ExpressionCreateTypeEnum.Subtraction:
      return ValueTypeEnum.enum.number
    case ExpressionCreateTypeEnum.And:
    case ExpressionCreateTypeEnum.Equals:
    case ExpressionCreateTypeEnum.GreaterThan:
    case ExpressionCreateTypeEnum.LessThan:
    case ExpressionCreateTypeEnum.Or:
      return ValueTypeEnum.enum.bool
    case ExpressionCreateTypeEnum.Conditional:
      return (expression as IConditionalOperatorCreate).type
    case ExpressionCreateTypeEnum.InputChoice:
    case ExpressionCreateTypeEnum.CaseIdentifier:
    case ExpressionCreateTypeEnum.ReviewOutcome:
      return ValueTypeEnum.enum.text
    case ExpressionCreateTypeEnum.IdentityConstant:
      return ValueTypeEnum.enum.identity
    case ExpressionCreateTypeEnum.FileConstant:
      return ValueTypeEnum.enum.file
    case ExpressionCreateTypeEnum.Today:
    case ExpressionCreateTypeEnum.RawDatetimeValue:
      return ValueTypeEnum.enum.datetime
    case ExpressionCreateTypeEnum.InputValue:
      return (expression as IInputValueCreate).type
    case ExpressionCreateTypeEnum.ExistingFormula:
      return (expression as IExistingFormulaCreate).type
    default:
      return null
  }
}

export function getParent(
  expressionMap: Record<number, ExpressionCreateType>,
  id: number,
): number | null {
  for (const [key, value] of Object.entries(expressionMap)) {
    switch (value.expressionType) {
      case ExpressionCreateTypeEnum.Addition:
      case ExpressionCreateTypeEnum.And:
      case ExpressionCreateTypeEnum.Equals:
      case ExpressionCreateTypeEnum.GreaterThan:
      case ExpressionCreateTypeEnum.LessThan:
      case ExpressionCreateTypeEnum.Maximum:
      case ExpressionCreateTypeEnum.Minimum:
      case ExpressionCreateTypeEnum.Multiplication:
      case ExpressionCreateTypeEnum.Or:
      case ExpressionCreateTypeEnum.Subtraction:
        const binaryExpressionCreate = value as IBinaryOperatorCreate
        if (binaryExpressionCreate.left === id || binaryExpressionCreate.right === id) {
          return Number(key)
        }
        break
      case ExpressionCreateTypeEnum.Conditional:
        const conditionalExpression = value as IConditionalOperatorCreate
        if (
          conditionalExpression.if === id ||
          conditionalExpression.then === id ||
          conditionalExpression.else === id
        ) {
          return Number(key)
        }
    }
  }
  return null
}

export function orderExpressions(
  expressionMap: Record<number, ExpressionCreateType>,
  id: number,
): number[] {
  const currentExpression = expressionMap[id]
  switch (currentExpression.expressionType) {
    case ExpressionCreateTypeEnum.AnyEmpty:
    case ExpressionCreateTypeEnum.Empty:
    case ExpressionCreateTypeEnum.ExistingFormula:
    case ExpressionCreateTypeEnum.InputChoice:
    case ExpressionCreateTypeEnum.InputValue:
    case ExpressionCreateTypeEnum.RawDatetimeValue:
    case ExpressionCreateTypeEnum.RawNumberValue:
    case ExpressionCreateTypeEnum.IdentityConstant:
    case ExpressionCreateTypeEnum.FileConstant:
    case ExpressionCreateTypeEnum.Today:
    case ExpressionCreateTypeEnum.CaseIdentifier:
    case ExpressionCreateTypeEnum.ReviewOutcome:
      return [currentExpression.id]
    case ExpressionCreateTypeEnum.Addition:
    case ExpressionCreateTypeEnum.And:
    case ExpressionCreateTypeEnum.Equals:
    case ExpressionCreateTypeEnum.GreaterThan:
    case ExpressionCreateTypeEnum.LessThan:
    case ExpressionCreateTypeEnum.Maximum:
    case ExpressionCreateTypeEnum.Minimum:
    case ExpressionCreateTypeEnum.Multiplication:
    case ExpressionCreateTypeEnum.Or:
    case ExpressionCreateTypeEnum.Subtraction:
      const binaryExpression = currentExpression as IBinaryOperatorCreate
      return [
        ...orderExpressions(expressionMap, binaryExpression.left),
        ...orderExpressions(expressionMap, binaryExpression.right),
        currentExpression.id,
      ]
    case ExpressionCreateTypeEnum.Conditional:
      const conditionalExpression = currentExpression as IConditionalOperatorCreate
      return [
        ...orderExpressions(expressionMap, conditionalExpression.if),
        ...orderExpressions(expressionMap, conditionalExpression.then),
        ...orderExpressions(expressionMap, conditionalExpression.else),
        currentExpression.id,
      ]
    default:
      return []
  }
}

export function getBinaryOperatorFromExpressionCreateType(
  expressionCreateType: ExpressionCreateTypeEnum,
): OperatorTypeEnum | null {
  // Only handles IBinaryOperatorCreate types
  switch (expressionCreateType) {
    case ExpressionCreateTypeEnum.Addition:
      return OperatorType.enum.addition
    case ExpressionCreateTypeEnum.And:
      return OperatorType.enum.and
    case ExpressionCreateTypeEnum.Equals:
      return OperatorType.enum.equals
    case ExpressionCreateTypeEnum.GreaterThan:
      return OperatorType.enum.greater_than
    case ExpressionCreateTypeEnum.LessThan:
      return OperatorType.enum.less_than
    case ExpressionCreateTypeEnum.Maximum:
      return OperatorType.enum.maximum
    case ExpressionCreateTypeEnum.Minimum:
      return OperatorType.enum.minimum
    case ExpressionCreateTypeEnum.Multiplication:
      return OperatorType.enum.multiplication
    case ExpressionCreateTypeEnum.Or:
      return OperatorType.enum.or
    case ExpressionCreateTypeEnum.Subtraction:
      return OperatorType.enum.subtraction
    default:
      return null
  }
}
